import { useRef, useEffect, useState, useCallback } from "react";
import { useMapStore, type PreviewTab } from "@/store/useMapStore";
import { useToast } from "@/hooks/use-toast";
import { type CustomizerConfig } from "@/config/customizerConfig";
import { parsePreviewError, type ParsedError } from "@/utils/errorParser";
import SimplePreview from "../SimplePreview";
import ProductPreview from "../ProductPreview";
import StageProgress from "./StageProgress";
import ThreeDViewer from "./ThreeDViewer";
import CancelPill from "./CancelPill";
import {
  RotateCcw,
  ZoomIn,
  AlertCircle,
  AlertTriangle,
  Clock,
  Wifi,
  MapPin,
  Monitor,
  Sparkles,
  Loader2,
  RefreshCw,
  X,
} from "lucide-react";

interface PreviewPanelProps {
  config: CustomizerConfig;
  fullscreen?: boolean;
}

function supportsWebGL(): boolean {
  try {
    const canvas = document.createElement("canvas");
    return !!(canvas.getContext("webgl2") || canvas.getContext("webgl"));
  } catch {
    return false;
  }
}

// ─── Tab definitions ───
const TABS: { id: PreviewTab; label: string }[] = [
  { id: "map", label: "My Map" },
  { id: "style", label: "Style Preview" },
  { id: "3d", label: "3D Model" },
];

// ─── TabBar ───
function TabBar({
  activeTab,
  onTabChange,
  hasCachedModel,
  isStale,
  isLoading,
}: {
  activeTab: PreviewTab;
  onTabChange: (tab: PreviewTab) => void;
  hasCachedModel: boolean;
  isStale: boolean;
  isLoading: boolean;
}) {
  return (
    <div
      role="tablist"
      aria-label="Preview tabs"
      className="flex items-center gap-1 px-3 pt-3 pb-2"
      style={{ backgroundColor: "var(--brand-bg)" }}
    >
      {TABS.map((tab) => {
        const isActive = activeTab === tab.id;
        const show3dDot = tab.id === "3d" && (isLoading || hasCachedModel);

        return (
          <button
            key={tab.id}
            role="tab"
            id={`tab-${tab.id}`}
            aria-selected={isActive}
            aria-controls={`tabpanel-${tab.id}`}
            onClick={() => onTabChange(tab.id)}
            className="relative px-4 py-2 rounded-full text-xs font-medium transition-all duration-200"
            style={{
              backgroundColor: isActive ? "var(--sage-600)" : "transparent",
              color: isActive ? "white" : "var(--brand-muted)",
              border: isActive ? "none" : "1px solid var(--brand-border)",
            }}
            onMouseEnter={(e) => {
              if (!isActive) {
                e.currentTarget.style.backgroundColor = "var(--sage-50)";
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive) {
                e.currentTarget.style.backgroundColor = "transparent";
              }
            }}
          >
            {tab.label}
            {show3dDot && (
              <span
                data-testid="3d-tab-indicator"
                className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ${isLoading ? "animate-pulse" : ""}`}
                style={{
                  backgroundColor: isLoading
                    ? "var(--sage-400)"
                    : isStale
                      ? "#f59e0b"
                      : "white",
                  boxShadow: isLoading
                    ? "none"
                    : isStale
                      ? "0 0 0 2px #fef3c7"
                      : "0 0 0 2px var(--sage-400)",
                }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

// ─── ThreeDEmptyState ───
function ThreeDEmptyState({
  onGenerate,
  canGenerate,
  isGenerating,
}: {
  onGenerate: () => void;
  canGenerate: boolean;
  isGenerating: boolean;
}) {
  return (
    <div className="w-full h-full flex items-center justify-center p-8">
      <div className="text-center max-w-xs">
        <div
          className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center"
          style={{
            border: "2px dashed var(--sage-300)",
            backgroundColor: "var(--sage-50)",
          }}
        >
          <Sparkles className="w-8 h-8" style={{ color: "var(--sage-400)" }} />
        </div>
        <div
          className="text-base font-semibold mb-1"
          style={{ color: "var(--brand-text)" }}
        >
          See your actual 3D model
        </div>
        <div
          className="text-sm mb-5"
          style={{ color: "var(--brand-muted)" }}
        >
          Takes ~30-60 seconds
        </div>
        <button
          onClick={onGenerate}
          disabled={!canGenerate || isGenerating}
          className="px-8 py-3 text-white rounded-xl text-sm font-semibold tracking-wide transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ backgroundColor: "var(--sage-400)" }}
          onMouseEnter={(e) => {
            if (!e.currentTarget.disabled) {
              e.currentTarget.style.backgroundColor = "var(--sage-500)";
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow = "0 4px 20px rgba(123, 158, 135, 0.35)";
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "var(--sage-400)";
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          {isGenerating ? "Generating..." : "Generate 3D Preview"}
        </button>
        {!canGenerate && (
          <p className="mt-2 text-xs" style={{ color: "var(--brand-muted)" }}>
            Select a location first
          </p>
        )}
      </div>
    </div>
  );
}

// ─── StaleBanner ───
function StaleBanner({
  reason,
  onUpdate,
}: {
  reason: string;
  onUpdate: () => void;
}) {
  return (
    <div
      className="absolute top-0 left-0 right-0 z-20 animate-slideDown"
      role="alert"
    >
      <div
        className="mx-3 mt-3 rounded-xl px-4 py-3 flex items-center justify-between gap-3 backdrop-blur-sm"
        style={{
          backgroundColor: "rgba(254, 243, 199, 0.95)",
          border: "1px solid #fcd34d",
        }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 text-amber-500" />
          <div className="min-w-0">
            <div className="text-xs font-medium text-amber-800">{reason}</div>
            <div className="text-[10px] text-amber-600">
              3D preview shows previous version
            </div>
          </div>
        </div>
        <button
          onClick={onUpdate}
          className="flex-shrink-0 px-3 py-1.5 text-white rounded-lg text-xs font-medium transition-colors"
          style={{ backgroundColor: "var(--sage-400)" }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "var(--sage-500)")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "var(--sage-400)")
          }
        >
          <RefreshCw className="w-3 h-3 inline mr-1" />
          Update 3D Preview
        </button>
      </div>
    </div>
  );
}

// ─── Error display constants ───
const ERROR_ICON_MAP = {
  Clock,
  Wifi,
  MapPin,
  AlertTriangle,
  AlertCircle,
} as const;

const ERROR_COLOR_MAP = {
  amber: { icon: "text-amber-500", bg: "bg-amber-50", border: "border-amber-200" },
  red: { icon: "text-red-500", bg: "bg-red-50", border: "border-red-200" },
  blue: { icon: "text-blue-500", bg: "bg-blue-50", border: "border-blue-200" },
} as const;

// ─── PreviewPanel ───
const PreviewPanel = ({ config, fullscreen = false }: PreviewPanelProps) => {
  const {
    previewState,
    previewStages,
    previewGlbUrl,
    previewError,
    previewRetryCount,
    startPreview,
    cancelPreview,
    revertToCache,
    resetPreview,
    setPreviewError,
    location,
    selectedPalette,
    palettes,
    // v3.13.0: tab/cache/stale state
    previewTab,
    setPreviewTab,
    cachedGlbUrl,
    isPreviewStale,
    staleReason,
  } = useMapStore();

  const { toast } = useToast();

  // v3.14.0: Cancel pill visibility (3s delay, managed at PreviewPanel level)
  const [showCancelPill, setShowCancelPill] = useState(false);
  const cancelTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (previewState === "loading") {
      cancelTimerRef.current = setTimeout(() => setShowCancelPill(true), 3000);
    } else {
      setShowCancelPill(false);
      if (cancelTimerRef.current) {
        clearTimeout(cancelTimerRef.current);
        cancelTimerRef.current = null;
      }
    }
    return () => {
      if (cancelTimerRef.current) {
        clearTimeout(cancelTimerRef.current);
        cancelTimerRef.current = null;
      }
    };
  }, [previewState]);

  const handleCancel = useCallback(() => {
    if (cachedGlbUrl) {
      // Flow B — stale refresh: abort request, revert to cached model
      revertToCache();
    } else {
      // Flow A — fresh generation: full reset to idle
      resetPreview();
    }
    toast({ description: "Preview generation cancelled" });
  }, [revertToCache, resetPreview, cachedGlbUrl, toast]);

  // Crossfade state for Flow A loading → result transition
  const [isViewerReady, setIsViewerReady] = useState(false);

  useEffect(() => {
    if (previewState === "loading") {
      setIsViewerReady(false);
    }
  }, [previewState]);

  // Refs for orbit control callbacks
  const resetViewRef = useRef<(() => void) | null>(null);
  const zoomInRef = useRef<(() => void) | null>(null);

  // Refs for focus management
  const stageListRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<HTMLDivElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  // WebGL detection (runs once)
  const hasWebGL = useRef(supportsWebGL());

  // Cleanup on unmount — cancel any in-flight preview job
  useEffect(() => {
    return () => {
      cancelPreview();
    };
  }, [cancelPreview]);

  const paletteDef = palettes.find((p) => p.id === selectedPalette);
  const paletteName = paletteDef?.displayName ?? "Classic Monochrome";
  const addressShort =
    location?.address?.split(",").slice(0, 2).join(",") ?? "Your location";

  const isGenerating = previewState === "loading";
  const canGenerate = !!location && !isGenerating;
  const hasCachedModel = !!cachedGlbUrl;

  // ─── Style tab content ───
  const renderStyleTab = () => (
    <div
      role="tabpanel"
      id="tabpanel-style"
      aria-labelledby="tab-style"
      className="h-full flex flex-col items-center p-4 pt-2"
      style={{
        background: "linear-gradient(to bottom right, var(--sage-50), var(--brand-bg))",
      }}
    >
      <div className="text-center mb-2">
        <div
          className="inline-block px-3 py-1 rounded-full text-xs"
          style={{
            backgroundColor: "var(--sage-50)",
            color: "var(--sage-600)",
            border: "1px solid var(--sage-200)",
          }}
        >
          Style Reference · {paletteName}
        </div>
        <p
          className="mt-1.5 text-[11px] leading-tight max-w-[280px] mx-auto"
          style={{ color: "var(--brand-muted)" }}
        >
          Reference image showing the color style. Your final product will
          feature your chosen location and may differ slightly.
        </p>
      </div>
      <div className="flex-1 min-h-0 w-full flex items-start justify-center">
        <div
          style={{
            maxWidth: 600 * 0.8,
            maxHeight: 560,
            width: "100%",
          }}
        >
          <ProductPreview />
        </div>
      </div>
    </div>
  );

  // ─── 3D tab rendering (Flow A vs Flow B) ───
  const render3DTab = () => {
    // WebGL unsupported
    if (!hasWebGL.current) {
      return (
        <div className="w-full h-full flex items-center justify-center p-8">
          <div
            className="rounded-xl border p-8 text-center max-w-sm"
            style={{
              backgroundColor: "var(--brand-card)",
              borderColor: "var(--brand-border)",
            }}
          >
            <Monitor className="w-12 h-12 mx-auto mb-3" style={{ color: "var(--sage-400)" }} />
            <div className="text-base font-semibold" style={{ color: "var(--brand-text)" }}>
              Your browser doesn&apos;t support 3D preview
            </div>
            <div className="text-sm mt-1" style={{ color: "var(--brand-muted)" }}>
              Try Chrome, Firefox, or Edge for the full 3D experience.
            </div>
          </div>
        </div>
      );
    }

    // ─── Flow B: Has cached model ───
    if (hasCachedModel) {
      return (
        <div className="relative h-full">
          {/* ThreeDViewer always shows cachedGlbUrl during loading; updated on completion */}
          <ThreeDViewer
            ref={viewerRef}
            glbUrl={cachedGlbUrl}
            onResetRef={resetViewRef}
            onZoomInRef={zoomInRef}
            onLoadComplete={() => setIsViewerReady(true)}
            onLoadError={(err) => setPreviewError(err)}
          />

          {/* Stale overlay + banner */}
          {isPreviewStale && previewState !== "loading" && previewState !== "error" && (
            <>
              <div
                className="absolute inset-0 z-10 pointer-events-none"
                style={{
                  backgroundColor: "rgba(0, 0, 0, 0.06)",
                  backdropFilter: "saturate(0.4)",
                  WebkitBackdropFilter: "saturate(0.4)",
                }}
              />
              <StaleBanner
                reason={staleReason ?? "Settings changed"}
                onUpdate={startPreview}
              />
            </>
          )}

          {/* Flow B loading overlay — spinner badge + cancel pill */}
          {previewState === "loading" && (
            <div className="absolute top-3 right-3 z-20 flex flex-col items-end gap-2">
              <div
                className="flex items-center gap-2 px-3 py-2 rounded-xl shadow-md"
                style={{
                  backgroundColor: "rgba(255,255,255,0.92)",
                  backdropFilter: "blur(8px)",
                }}
              >
                <Loader2 className="w-4 h-4 animate-spin" style={{ color: "var(--sage-500)" }} />
                <span className="text-xs font-medium" style={{ color: "var(--sage-600)" }}>
                  Updating...
                </span>
              </div>
              {showCancelPill && (
                <button
                  onClick={handleCancel}
                  className="flex items-center gap-2 px-4 py-2
                             rounded-full text-sm font-medium shadow-lg border backdrop-blur-sm
                             transition-colors duration-200
                             hover:bg-red-50 hover:border-red-200 hover:text-red-600
                             focus-visible:ring-2 focus-visible:ring-[var(--sage-400)] focus-visible:ring-offset-2"
                  style={{
                    background: "rgba(255, 255, 255, 0.95)",
                    borderColor: "var(--brand-border)",
                    color: "var(--brand-muted)",
                    animation:
                      "cancel-pill-in 0.5s cubic-bezier(0.22, 1, 0.36, 1) forwards, cancel-pill-pulse 2s ease-in-out 1s infinite",
                  }}
                  aria-label="Cancel preview generation"
                >
                  <span
                    className="w-5 h-5 rounded-full flex items-center justify-center transition-colors"
                    style={{ background: "var(--sage-50)" }}
                  >
                    <X className="w-3 h-3" />
                  </span>
                  Cancel
                </button>
              )}
            </div>
          )}

          {/* Flow B error overlay — non-destructive banner */}
          {previewState === "error" && (
            <div className="absolute top-3 left-3 right-3 z-20">
              <div
                ref={errorRef}
                className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl shadow-md"
                role="alert"
                tabIndex={-1}
                style={{
                  backgroundColor: "rgba(239, 68, 68, 0.95)",
                  backdropFilter: "blur(8px)",
                }}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <AlertCircle className="w-3.5 h-3.5 text-white flex-shrink-0" />
                  <span className="text-xs font-medium text-white truncate">
                    Update failed
                  </span>
                </div>
                {previewRetryCount < 3 && (
                  <button
                    onClick={startPreview}
                    className="flex-shrink-0 px-3 py-1 rounded-lg text-xs font-semibold transition-colors"
                    style={{
                      backgroundColor: "rgba(255,255,255,0.25)",
                      color: "white",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.4)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.25)";
                    }}
                  >
                    Retry
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Orbit controls */}
          {isViewerReady && previewState !== "loading" && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
              <button
                onClick={() => resetViewRef.current?.()}
                className="w-9 h-9 rounded-lg bg-white shadow-md border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50"
                aria-label="Reset view"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
              <button
                onClick={() => zoomInRef.current?.()}
                className="w-9 h-9 rounded-lg bg-white shadow-md border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50"
                aria-label="Zoom in"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
              <span className="text-xs text-gray-400 ml-2">Drag to rotate</span>
            </div>
          )}
        </div>
      );
    }

    // ─── Flow A: No cached model ───

    // Loading — full-screen StageProgress with crossfade
    if (previewState === "loading" || (previewState === "result" && previewGlbUrl)) {
      return (
        <div className="relative h-full">
          {/* Loading overlay — visible until viewer is ready */}
          <div
            className={`absolute inset-0 transition-opacity duration-500 ${
              isViewerReady ? "opacity-0 pointer-events-none" : "opacity-100"
            }`}
          >
            <StageProgress
              ref={stageListRef}
              stages={previewStages}
              address={addressShort}
              paletteName={paletteName}
            />
            {showCancelPill && <CancelPill onCancel={handleCancel} />}
          </div>

          {/* ThreeDViewer — renders when GLB URL available */}
          {previewState === "result" && previewGlbUrl && (
            <div
              className={`absolute inset-0 transition-opacity duration-500 ${
                isViewerReady ? "opacity-100" : "opacity-0"
              }`}
            >
              <ThreeDViewer
                ref={viewerRef}
                glbUrl={previewGlbUrl}
                onResetRef={resetViewRef}
                onZoomInRef={zoomInRef}
                onLoadComplete={() => setIsViewerReady(true)}
                onLoadError={(err) => setPreviewError(err)}
              />

              {/* Orbit controls */}
              {isViewerReady && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
                  <button
                    onClick={() => resetViewRef.current?.()}
                    className="w-9 h-9 rounded-lg bg-white shadow-md border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50"
                    aria-label="Reset view"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => zoomInRef.current?.()}
                    className="w-9 h-9 rounded-lg bg-white shadow-md border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50"
                    aria-label="Zoom in"
                  >
                    <ZoomIn className="w-4 h-4" />
                  </button>
                  <span className="text-xs text-gray-400 ml-2">Drag to rotate</span>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    // Error — full-screen display (Flow A: no cached model to preserve)
    if (previewState === "error") {
      const parsed: ParsedError = parsePreviewError(previewError);
      const maxRetriesReached = previewRetryCount >= 3;
      const IconComponent = ERROR_ICON_MAP[parsed.icon];
      const colors = ERROR_COLOR_MAP[parsed.color];

      return (
        <div className="w-full h-full flex items-center justify-center p-8">
          <div
            ref={errorRef}
            className={`rounded-xl ${colors.border} border p-8 text-center max-w-sm ${colors.bg}`}
            role="alert"
            tabIndex={-1}
          >
            <IconComponent className={`w-12 h-12 ${colors.icon} mx-auto mb-3`} />
            <div className="text-base font-semibold text-gray-900">{parsed.title}</div>
            <div className="text-sm mt-1 text-gray-600">{parsed.message}</div>
            {parsed.retriable && !maxRetriesReached ? (
              <button
                onClick={startPreview}
                className="mt-4 px-6 py-2 text-white rounded-lg text-sm font-medium transition-colors"
                style={{ backgroundColor: "var(--sage-400)" }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "var(--sage-500)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "var(--sage-400)";
                }}
              >
                Try Again
              </button>
            ) : maxRetriesReached ? (
              <a
                href="mailto:support@geoforge3d.com"
                className="mt-4 inline-block px-6 py-2 text-white rounded-lg text-sm font-medium transition-colors"
                style={{ backgroundColor: "var(--sage-400)" }}
              >
                Contact Support
              </a>
            ) : (
              <p className="mt-3 text-xs text-gray-400">
                Try adjusting your selection or contact support if the issue persists.
              </p>
            )}
          </div>
        </div>
      );
    }

    // Default — empty state with CTA
    return (
      <ThreeDEmptyState
        onGenerate={startPreview}
        canGenerate={canGenerate}
        isGenerating={isGenerating}
      />
    );
  };

  // ─── Main render ───
  return (
    <div className="w-full h-full flex flex-col bg-[var(--brand-bg)]">
      <TabBar
        activeTab={previewTab}
        onTabChange={setPreviewTab}
        hasCachedModel={hasCachedModel}
        isStale={isPreviewStale}
        isLoading={isGenerating}
      />
      <div className="flex-1 min-h-0 relative overflow-hidden">
        {previewTab === "map" && (
          <div
            role="tabpanel"
            id="tabpanel-map"
            aria-labelledby="tab-map"
            className="h-full [&>div]:items-start [&>div]:pt-2"
          >
            <SimplePreview config={config} fullscreen={fullscreen} />
          </div>
        )}
        {previewTab === "style" && renderStyleTab()}
        {previewTab === "3d" && (
          <div
            role="tabpanel"
            id="tabpanel-3d"
            aria-labelledby="tab-3d"
            className="h-full"
          >
            {render3DTab()}
          </div>
        )}
      </div>
    </div>
  );
};

export default PreviewPanel;
